import { Sine, TimelineLite, TweenLite } from "gsap";
import * as HLF from "html-living-framework";
import { Application } from "../Application";
import { AppProgress } from "../Data/AppProgress";
import { CaptionToken } from "../Data/CaptionToken";
import { GameType } from "../Data/GameData";
import * as SoundEffect from "../Data/SoundEffect";
import * as Topics from "../Data/Topics";

export abstract class GalleryItem extends PIXI.Container implements HLF.Input.IFocusable {
    protected static readonly SCALE_NORMAL: number = 1.0;
    protected static readonly SCALE_OVER: number = 1.05;
    protected static readonly SCALE_DOWN: number = 0.95;

    protected _gameType: GameType;
    protected _visuals: PIXI.Container;
    protected _cached: PIXI.Sprite | undefined;
    protected _saveData: any;
    protected _callback: (pItem: GalleryItem) => void;
    protected _eventData: PIXI.interaction.InteractionData | undefined;
    protected _clickedSfx: string | undefined;
    protected _hoverVO: string | undefined;
    protected _homePos!: PIXI.Point;
    protected _homeScale!: number;
    protected _presentScale!: number;
    protected _targetState!: string;
    protected _size: PIXI.Point;
    protected _canPlayVo: boolean;
    protected _pubSubTokens: any[];

    constructor(pGameType: GameType, pCallback: (pItem: GalleryItem) => void) {
        super();
        this._gameType = pGameType;
        this._callback = pCallback;
        this._canPlayVo = true;
        this._pubSubTokens = new Array<any>();

        this._size = Application.instance.size;
        this._clickedSfx = SoundEffect.UI_CLICK;

        this._visuals = new PIXI.Container();
        this.addChild(this._visuals);

        this.interactive = true;
        this.buttonMode = true;

        this.on(HLF.Input.Events.POINTER_OVER, this.onPointerOver);
        this.on(HLF.Input.Events.POINTER_DOWN, this.onPointerDown);
        this.on(HLF.Input.Events.POINTER_UP, this.onPointerUp);
        this.on(HLF.Input.Events.POINTER_OUT, this.onPointerOut);

        this._pubSubTokens.push(PubSub.subscribe(Topics.FUN_FACT_START, () => { this._canPlayVo = false; }));
        this._pubSubTokens.push(PubSub.subscribe(Topics.FUN_FACT_COMPLETE, () => { this._canPlayVo = true; }));
    }

    public get targetState(): string {
        return this._targetState;
    }

    public destroy(pOptions?: boolean | PIXI.DestroyOptions): void {
        for (let i = 0; i < this._pubSubTokens.length; ++i) {
            PubSub.unsubscribe(this._pubSubTokens[i]);
        }
        super.destroy(pOptions);
    }

    public setHitAreaDimensions(pWidth: number, pHeight: number): void {
        this.hitArea = new PIXI.Rectangle(
            -pWidth * 0.5,
            -pHeight * 0.5,
            pWidth,
            pHeight,
        );
    }

    public presentItem(): TimelineLite {
        this.interactive = false;

        const timeline: TimelineLite = new TimelineLite();
        return timeline;
    }

    public animateItem(): TimelineLite {
        const timeline: TimelineLite = new TimelineLite();
        return timeline;
    }

    public moveItemToWall(): TimelineLite {
        const timeline: TimelineLite = new TimelineLite();
        return timeline;
    }

    public performHangUp(): TimelineLite {
        const timeline: TimelineLite = new TimelineLite();
        timeline.call(() => {
            this.interactive = true;
        });
        return timeline;
    }

    public abstract cacheVisuals(): PIXI.Texture;

    public onFocusBegin(): void {
        PubSub.publishSync(Topics.KEYBOARD_FOCUS_BEGIN, this);
        this.playHoverVO();
    }

    public onFocusActivated(): void {
        if (this.interactive) {
            this.playClickedSfx();
            this._callback(this);
        }
    }

    public onFocusEnd(): void {
        PubSub.publishSync(Topics.KEYBOARD_FOCUS_END, this);
    }

    public getFocusPosition(): PIXI.Point {
        return this._visuals.getGlobalPosition();
    }

    public getFocusSize(): PIXI.Point {
        return new PIXI.Point(this.width, this.height);
    }

    // Call this function in the child class constructor, since any member variables will be reset to undefined once
    // super.constructor finishes execution
    protected createVisuals(): void {
        if (AppProgress.recentGame === this._gameType) {
            this.createVisualComplete();
        }
        else if (AppProgress.games[this._gameType].recentItemIndex !== -1) {
            this.createVisualCached();
        }
        else {
            this.createVisualDefault();
        }
    }

    protected abstract createVisualDefault(): void;
    protected abstract createVisualComplete(): void;

    protected createVisualCached(): void {
        this._cached = new PIXI.Sprite(AppProgress.games[this._gameType].cachedGalleryVisuals!);
        this._cached.anchor.set(0.5);
        this._visuals.addChild(this._cached);
        this._visuals.position.copy(this._homePos);
    }

    protected onPointerOver(): void {
        this.animateScale(GalleryItem.SCALE_OVER, 0.15);
        this.playHoverVO();
    }

    protected onPointerOut(): void {
        this.animateScale(GalleryItem.SCALE_NORMAL, 0.15);
    }

    protected onPointerDown(pEvent: PIXI.interaction.InteractionEvent): void {
        this._eventData = pEvent.data;
        this.animateScale(GalleryItem.SCALE_DOWN, 0.15);
    }

    protected onPointerUp(): void {
        if (this._eventData !== undefined) {
            this._callback(this);
            this.playClickedSfx();
            this.animateScale(GalleryItem.SCALE_OVER, 0.15);
        }
    }

    protected playHoverVO(): void {
        if (this._hoverVO !== undefined && this._canPlayVo) {
            PubSub.publishSync(HLF.PLAY_AUDIO, new HLF.AudioToken(this._hoverVO, 1, false, HLF.AudioCategory.VO));
            PubSub.publishSync(Topics.PLAY_CAPTION, new CaptionToken(this._hoverVO));
        }
    }

    protected playClickedSfx(): void {
        if (this._clickedSfx !== undefined) {
            PubSub.publishSync(HLF.PLAY_AUDIO, new HLF.AudioToken(this._clickedSfx, 1, false, HLF.AudioCategory.SFX));
        }
    }

    private animateScale(pScale: number, pDur: number): void {
        TweenLite.to(this._visuals.scale, pDur, {
            x: pScale,
            y: pScale,
            ease: Sine.easeInOut,
        });
    }
}
